import { Loading } from '@social-solutions/component-library';
import DeleteConfigModal from 'components/DeleteConfigModal/DeleteConfigModal';
import ConfigurationTable from 'components/Generics/Table/ConfigurationTable';
import { Configuration as IConfiguration } from 'models/schema-generated-models/idp/configuration';
import React, { useContext, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { TypeOptions } from 'react-toastify';
import { ClientFactory, IdentityProviderService } from 'services';
import { SSOAPINotFoundError } from 'services/errors';
import { Notification } from 'services/notification-service';
import ConfigurationDetails from '../ConfigurationDetails/ConfigurationDetails';
import * as S from './IdentityProviderList.styles';
import { IntegrationContext } from 'helpers/integration/IntegrationContext';

export interface IConfigResponse {
    Configurations: string;
    LoginURL: string;
}

export interface IIdentityProvidersListProps {
    idpList: IConfiguration[];
    setIdpList: (idpList: IConfiguration[]) => void;
    idpListLoaded: boolean;
}

export default function IdentityProviderList({idpList, setIdpList, idpListLoaded}: IIdentityProvidersListProps): JSX.Element {
    const { t } = useTranslation();
    const integration = useContext(IntegrationContext);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState({} as IConfiguration);
    const loader = {
        color: 'black',
        size: '40px'
    };
    const client = useMemo(() => ClientFactory.createClient(integration.getTenantId(), integration.getIntegrationType(), integration.getSessionHeaders()), [integration]);
    const identityProviderService: IdentityProviderService = useMemo(() => new IdentityProviderService(client), [client]);
    const copiedNotification: Notification = useMemo(() => new Notification(t('notifications:NOTIFY_IDP_LOGIN_URL_COPIED'), 'copied-notification', { type: 'info', autoClose: 2000 }),[t]);
    const deleteProcessNotification: Notification = useMemo(() => new Notification(t('notifications:NOTIFICATION_IDP_DELETE_LOADING'), 'delete-notification', { type: 'info', isLoading: true }), [t]);
    const toggleNotification: Notification = useMemo(() => new Notification(t('notifications:NOTIFY_UPDATING'), 'toggle-notification', { type: 'info', isLoading: true }),[t]);

    const deleted = React.useCallback((provider: IConfiguration) => {
        const newList = idpList.filter((idp) => idp.ProviderName !== provider.ProviderName);
        setIdpList(newList);
    }, [idpList, setIdpList]);

    const updateMessage = React.useCallback((message: string, type: TypeOptions) => {
        deleteProcessNotification.update({ render: message, type: type });
    },[deleteProcessNotification]);

    // TODO: Lift functions that require service calls to parent component to keep the component dumb
    const removeConfiguration = React.useCallback(async (config: IConfiguration) => {

        deleteProcessNotification.show();
        const defaultErrorMessage = `${t('notifications:NOTIFICATION_IDP_DELETE_FAILED')} ${t('notifications:GENERAL_WARNING')}`;

        setModalIsOpen(false);

        if (config.ProviderName) {
            try {
                await identityProviderService.deleteIdentityProvider(config.ConfigurationName, config.ProviderName);
                updateMessage(t('notifications:NOTIFICATION_IDP_DELETE_SUCCEED'), 'success');
                deleted(config);
            } catch (error) {
                let errorMessage;
                if (error instanceof SSOAPINotFoundError) {
                    errorMessage = t('notifications:NOTIFICATION_IDP_DELETE_FAILED');
                } else {
                    errorMessage = (error instanceof Error && error.message) || defaultErrorMessage;
                }
                updateMessage(errorMessage, 'error');
            }
        }
        else {
            updateMessage(defaultErrorMessage, 'error');
        }
    }, [deleteProcessNotification, t, setModalIsOpen, identityProviderService, updateMessage, deleted]);

    const onCopied = () => {
        copiedNotification.show();
    };

    const onDeleted = (config: IConfiguration) => {
        setSelectedConfig(config);
        setModalIsOpen(true);
    };

    const onToggled = async (configurationName: string, providerName: string, status: boolean) => {
        toggleNotification.show();
        try {
            const responseBody = await identityProviderService.toggleIdentityProviderStatus(configurationName, providerName, status);
            if (responseBody && responseBody.message) {
                return toggleNotification.update({ render: responseBody.message, type: 'info' });
            }
        } catch(error) {
            toggleNotification.update({ render: t('notifications:NOTIFY_IDP_UPDATE_FAIL_TO_TOGGLE'), type: 'error' });
        }
    };

    return (
        <>
            {modalIsOpen ? (
                <DeleteConfigModal
                    closeModal={() => setModalIsOpen(false)}
                    saveData={() => removeConfiguration(selectedConfig)}
                />
            ) : null}
            <S.Card>
                {idpList.length !== 0 ? <h1><Trans>CONFIGURATION.TABLE_TITLE</Trans></h1> : <></> }
                {idpListLoaded ?
                    idpList.length > 0 ?
                        <ConfigurationTable onCopied={onCopied} onDeleted={onDeleted} onToggled={onToggled} configurations={idpList} />
                        : <ConfigurationDetails />
                    : <S.CardBody>
                        <Loading color={loader.color} fontSize={loader.size} type={'spin'} />
                    </S.CardBody>
                }
            </S.Card>
        </>
    );
}
